<script setup lang="ts">
  import { onMounted, onUnmounted } from 'vue';
  import AsteriskText from '../Forms/AsteriskText.vue';

  const props = defineProps<{
    visible: boolean;
    loading: boolean;
    modalTitle: string;
    isAdvance?: boolean;
    linkedExpenses?: number[];
  }>();

  const modalState = reactive<{
    edit_reason: string;
    password: string;
  }>({
    edit_reason: '',
    password: ''
  });

  const hasLinkedExpenses = computed(() => (props.linkedExpenses?.length ?? 0) > 0);
  const updateModalRef = ref();
  const updateModalRules = {
    edit_reason: [{ required: true, message: 'Reason is required!', trigger: 'submit' }],
    password: [{ required: true, message: 'Password is required!', trigger: 'submit' }]
  };

  const emit = defineEmits(['update:update', 'update:cancel']);

  const handleModalOk = async () => {
    try {
      const isValid = await updateModalRef.value.validate();
      if (isValid) {
        emit('update:update', { ...modalState });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleModalCancel = () => {
    emit('update:cancel');
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent default form submission
      handleModalOk();
    }
  };

  const getLinkedExpensesUrl = computed(() => {
    if (props.linkedExpenses && props.linkedExpenses.length > 0) {
      return `/expenses?expenseUid=${props.linkedExpenses.join(',')}`;
    }
    return '/expenses';
  });

  onMounted(() => {
    document.addEventListener('keydown', handleKeyDown);
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown);
  });
</script>

<template>
  <a-modal
    :open="props.visible"
    :title="props.modalTitle"
    :class="isAdvance ? 'account-info-modal !w-[600px]' : 'account-info-modal'"
    ok-text="Update"
    @ok="handleModalOk"
    @cancel="handleModalCancel"
  >
    <div
      v-if="isAdvance && hasLinkedExpenses"
      id="advance-expense-warning"
      class="px-3 py-2 border border-[#FDBA74] bg-[#FFF7ED] rounded-lg flex items-start mb-4"
    >
      <div class="flex flex-row gap-2 items-start">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M16.5 8.5C16.5 10.6217 15.6571 12.6566 14.1569 14.1569C12.6566 15.6571 10.6217 16.5 8.5 16.5C6.37827 16.5 4.34344 15.6571 2.84315 14.1569C1.34285 12.6566 0.5 10.6217 0.5 8.5C0.5 6.37827 1.34285 4.34344 2.84315 2.84315C4.34344 1.34285 6.37827 0.5 8.5 0.5C10.6217 0.5 12.6566 1.34285 14.1569 2.84315C15.6571 4.34344 16.5 6.37827 16.5 8.5ZM9.5 12.5C9.5 12.7652 9.39464 13.0196 9.20711 13.2071C9.01957 13.3946 8.76522 13.5 8.5 13.5C8.23478 13.5 7.98043 13.3946 7.79289 13.2071C7.60536 13.0196 7.5 12.7652 7.5 12.5C7.5 12.2348 7.60536 11.9804 7.79289 11.7929C7.98043 11.6054 8.23478 11.5 8.5 11.5C8.76522 11.5 9.01957 11.6054 9.20711 11.7929C9.39464 11.9804 9.5 12.2348 9.5 12.5ZM8.5 3.5C8.23478 3.5 7.98043 3.60536 7.79289 3.79289C7.60536 3.98043 7.5 4.23478 7.5 4.5V8.5C7.5 8.76522 7.60536 9.01957 7.79289 9.20711C7.98043 9.39464 8.23478 9.5 8.5 9.5C8.76522 9.5 9.01957 9.39464 9.20711 9.20711C9.39464 9.01957 9.5 8.76522 9.5 8.5V4.5C9.5 4.23478 9.39464 3.98043 9.20711 3.79289C9.01957 3.60536 8.76522 3.5 8.5 3.5Z"
            fill="#C2410C"
          />
        </svg>
        <div class="flex flex-col gap-1">
          <p class="text-[#C2410C] font-medium leading-4 text-sm">Advance is linked with expenses.</p>
          <p class="text-[#000000] font-normal leading-5 text-sm">
            Cancelling only allowed after delinking expense.
            <NuxtLink :to="getLinkedExpensesUrl" class="text-blue-700 underline hover:text-blue-700 hover:no-underline"
              >See linked expenses</NuxtLink
            >
          </p>
        </div>
      </div>
    </div>
    <a-form
      ref="updateModalRef"
      layout="vertical"
      label-align="left"
      class="flex flex-col gap-3"
      :label-col="{ style: { width: '183px' } }"
      :colon="false"
      :rules="updateModalRules"
      :model="modalState"
      :hide-required-mark="true"
      :disabled="props.loading"
      submit-on-enter
    >
      <a-form-item name="edit_reason" class="!sm-medium">
        <template #label>
          <AsteriskText label="Reason for update" />
        </template>
        <a-textarea v-model:value="modalState.edit_reason" class="w-[547px]" :rows="3" />
      </a-form-item>
      <a-form-item name="password" class="!sm-medium">
        <template #label>
          <AsteriskText label="Edit Password" />
        </template>
        <a-input v-model:value="modalState.password" type="password" class="w-[248px]" />
      </a-form-item>
    </a-form>
    <template #footer>
      <div class="flex gap-1 w-full justify-end border-t border-gray-300 p-3 bg-gray-50 rounded-b-lg">
        <a-button
          class="rounded text-sm font-medium border border-gray-300 text-gray-500 px-3 py-1.5"
          @click="handleModalCancel"
          >Cancel</a-button
        >
        <a-button
          v-if="!isAdvance"
          class="px-3 border border-blue-700 rounded py-1.5 flex items-center justify-center text-sm font-medium"
          :loading="props.loading"
          type="primary"
          @click="handleModalOk"
          >Update</a-button
        >
        <a-button
          v-if="isAdvance"
          class="px-3 border !border-red-600 rounded py-1.5 flex items-center justify-center text-sm font-medium !bg-red-600 hover:!bg-opacity-80 hover:!border-opacity-80"
          :loading="props.loading"
          type="primary"
          danger
          @click="handleModalOk"
          >Cancel Advance</a-button
        >
      </div>
    </template>
  </a-modal>
</template>

<style scoped>
  @import '~/assets/css/modal.css';

  .ant-form-item {
    margin-bottom: 0px !important;
  }
</style>
